import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { CONTENT_GROUPS, STAFF_GROUPS } from '@fin/shared/enums';

import { AuthProvider } from './lib/auth';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppShell } from './components/AppShell';
import { InstallPrompt } from './components/InstallPrompt';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { TodayPage } from './pages/Today';
import { WorkersPage } from './pages/Workers';
import WorkerDetailPage from './pages/WorkerDetail';
import { CustomersPage } from './pages/Customers';
import CustomerDetailPage from './pages/CustomerDetail';
import { JobsPage } from './pages/Jobs';
import { JobDetailPage } from './pages/JobDetail';
import InvoicesPage from './pages/Invoices';
import CalendarPage from './pages/Calendar';
import { FeedbackPage } from './pages/Feedback';
import SettingsPage from './pages/Settings';
import HowItWorksPage from './pages/HowItWorks';
import ContentPage from './pages/Content';
import ContentEditorPage from './pages/ContentEditor';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 } },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <InstallPrompt />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              element={
                <ProtectedRoute requireGroup={[...STAFF_GROUPS, 'marketing']}>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              {/* CRM pages — staff only. `marketing` is bounced to /content. */}
              <Route
                element={
                  <ProtectedRoute requireGroup={[...STAFF_GROUPS]}>
                    <Outlet />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Dashboard />} />
                <Route path="/today" element={<TodayPage />} />
                <Route path="/workers" element={<WorkersPage />} />
                <Route path="/workers/:id" element={<WorkerDetailPage />} />
                <Route path="/customers" element={<CustomersPage />} />
                <Route path="/customers/:id" element={<CustomerDetailPage />} />
                <Route path="/jobs" element={<JobsPage />} />
                <Route path="/jobs/:id" element={<JobDetailPage />} />
                <Route path="/invoices" element={<InvoicesPage />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/feedback" element={<FeedbackPage />} />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/settings" element={<SettingsPage />} />
              </Route>
              <Route
                path="/content"
                element={
                  <ProtectedRoute requireGroup={[...CONTENT_GROUPS]}>
                    <ContentPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/content/:id"
                element={
                  <ProtectedRoute requireGroup={[...CONTENT_GROUPS]}>
                    <ContentEditorPage />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
